export const CONTEXT_LINES = 3;

const MIN_COLLAPSED_LINES = 2;

export type CollapsibleEntry<T> = { type: 'line'; line: T } | { type: 'collapsed'; key: string; lines: T[] };

interface LineRun<T> {
  unchanged: boolean;
  start: number;
  lines: T[];
}

const toLineEntries = <T>(lines: T[]): CollapsibleEntry<T>[] =>
  lines.map((line): CollapsibleEntry<T> => ({ type: 'line', line }));

const splitIntoRuns = <T>(lines: T[], isUnchanged: (line: T) => boolean): LineRun<T>[] => {
  const runs: LineRun<T>[] = [];
  for (const [index, line] of lines.entries()) {
    const unchanged = isUnchanged(line);
    const currentRun = runs.at(-1);
    if (currentRun?.unchanged === unchanged) {
      currentRun.lines.push(line);
    } else {
      runs.push({ unchanged, start: index, lines: [line] });
    }
  }
  return runs;
};

const collapseRun = <T>(
  run: LineRun<T>,
  { isFirst, isLast }: { isFirst: boolean; isLast: boolean }
): CollapsibleEntry<T>[] => {
  const contextBefore = isFirst ? 0 : CONTEXT_LINES;
  const contextAfter = isLast ? 0 : CONTEXT_LINES;
  const hiddenEnd = run.lines.length - contextAfter;
  const hiddenLines = run.lines.slice(contextBefore, hiddenEnd);

  if (hiddenLines.length < MIN_COLLAPSED_LINES) {
    return toLineEntries(run.lines);
  }

  return [
    ...toLineEntries(run.lines.slice(0, contextBefore)),
    { type: 'collapsed', key: `unchanged-${run.start + contextBefore}`, lines: hiddenLines },
    ...toLineEntries(run.lines.slice(hiddenEnd)),
  ];
};

export const collapseUnchangedLines = <T>(lines: T[], isUnchanged: (line: T) => boolean): CollapsibleEntry<T>[] => {
  const runs = splitIntoRuns(lines, isUnchanged);
  return runs.flatMap((run, index) =>
    run.unchanged
      ? collapseRun(run, { isFirst: index === 0, isLast: index === runs.length - 1 })
      : toLineEntries(run.lines)
  );
};

export const expandCollapsedEntries = <T>(
  entries: CollapsibleEntry<T>[],
  expandedKeys: ReadonlySet<string>
): CollapsibleEntry<T>[] =>
  entries.flatMap(entry =>
    entry.type === 'collapsed' && expandedKeys.has(entry.key) ? toLineEntries(entry.lines) : [entry]
  );
